import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  MDBBtn,
  MDBCol,
  MDBIcon,
  MDBInput,
  MDBModal,
  MDBModalBody,
  MDBModalHeader,
  MDBRow,
} from "mdbreact";
import Swal from "sweetalert2";
import { Memberships } from "../../../../../../../../services/fakeDb";
import { axioKit } from "../../../../../../../../services/utilities";
import { SETSOURCES } from "../../../../../../../../services/redux/slices/assets/providers";
import AddressSelect from "../../../../../../../../components/searchables/addressSelect";
import { Select } from "../../../../../../../../components/customizable";
import "../../../../../../../../components/searchables/physicians/style.css";

const _address = {
  region: "REGION III (CENTRAL LUZON)",
  province: "NUEVA ECIJA",
  city: "GENERAL TINIO (PAPAYA)",
  barangay: "Pias",
};

// every word of the key must be found in the label
const matches = (label = "", key = "") => {
  const text = String(label).toUpperCase();
  return key
    .toUpperCase()
    .split(/\s+/)
    .filter(Boolean)
    .every((word) => text.includes(word));
};

const branchLabel = ({ displayname = "", name = "" } = {}) =>
  displayname && name && displayname !== name
    ? `${name} - ${displayname}`
    : displayname || name;

/**
 * Source picker of the POS.
 * Searches the sources tagged to the branch first, then the roster of branches
 * (tag it as a new source), and lastly registers a brand new source.
 */
export default function PickSource({
  scType = "rfr",
  srcName = "",
  sources = [],
  value = {},
  getLabel = () => "",
  disabled,
  onSelect = () => {},
}) {
  const { token, activePlatform } = useSelector(({ auth }) => auth),
    {
      collections = [],
      categories = [],
      contractCategories = [],
    } = useSelector(({ providers }) => providers),
    [query, setQuery] = useState(""),
    [isFocused, setIsFocused] = useState(false),
    [roster, setRoster] = useState(null), // null: roster not searched yet
    [isSearching, setIsSearching] = useState(false),
    [isSaving, setIsSaving] = useState(false),
    [form, setForm] = useState(null), // register form, null when closed
    searchId = useRef(0),
    dispatch = useDispatch();

  const isInhouse = scType === "inhouse",
    { branchId } = activePlatform || {},
    selectedLabel = value?._id ? getLabel(value) : "";

  // show the selected source, and clear the search when it changes
  useEffect(() => {
    setQuery(selectedLabel);
    setRoster(null);
  }, [selectedLabel, scType]);

  const key = query.trim(),
    isTyping = key && key !== selectedLabel,
    tagged = isTyping
      ? sources.filter((source) => matches(getLabel(source), key))
      : sources;

  const tiers =
    scType === "mbs"
      ? Memberships.collections
      : scType === "ctr"
      ? contractCategories
      : [];
  const tierKey = scType === "mbs" ? "membership" : "contract";

  const handleChange = ({ target }) => {
    setQuery(target.value);
    setRoster(null);
    if (!target.value) onSelect({});
  };

  const handlePick = (source) => {
    setQuery(getLabel(source));
    setRoster(null);
    setIsFocused(false);
    onSelect(source);
  };

  const searchRoster = async () => {
    const id = ++searchId.current;
    setIsSearching(true);
    try {
      const branches = await axioKit.universal(
        "assets/branches/search",
        token,
        { searchKey: key },
      );
      if (id !== searchId.current) return;
      // hide your own branch and the ones already tagged as this source type
      const taggedIds = sources.map(({ clients }) => String(clients?._id));
      setRoster(
        (branches || []).filter(
          ({ _id }) =>
            String(_id) !== String(branchId) &&
            !taggedIds.includes(String(_id)),
        ),
      );
    } catch (error) {
      if (id === searchId.current) setRoster([]);
      Swal.fire({ icon: "error", title: "Search failed", text: error.message });
    } finally {
      if (id === searchId.current) setIsSearching(false);
    }
  };

  // keep the POS cache and the store in sync, then select the new source
  const addSource = (provider) => {
    const next = [provider, ...collections];
    dispatch(SETSOURCES(next));
    localStorage.setItem(`source_${branchId}`, JSON.stringify(next));
    handlePick(provider);
  };

  const askTier = async () => {
    if (!tiers.length) return "";
    const { value: tier } = await Swal.fire({
      title: `Select ${tierKey}`,
      input: "select",
      inputOptions: Object.fromEntries(
        tiers.map(({ value, text }) => [value, text]),
      ),
      inputPlaceholder: `Select ${tierKey}`,
      showCancelButton: true,
      inputValidator: (value) => !value && `Please select a ${tierKey}.`,
    });
    return tier;
  };

  const handleTag = async (branch) => {
    const name = branchLabel(branch);
    const { isConfirmed } = await Swal.fire({
      title: name,
      text: `Tag this branch as your ${srcName.toLowerCase()} source?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, tag it!",
    });
    if (!isConfirmed) return;

    const tier = await askTier();
    if (tiers.length && !tier) return;

    setIsSaving(true);
    try {
      const provider = await axioKit.save(
        "assets/providers",
        {
          clients: branch._id,
          status: "approved",
          category: scType,
          vendors: branchId,
          ...(tier && { [tierKey]: tier }),
        },
        token,
      );
      // the saved provider does not carry the company of the source
      addSource({
        ...provider,
        clients: { ...branch, ...provider.clients },
      });
    } catch (error) {
      Swal.fire({ icon: "error", title: "Tagging failed", text: error.message });
    } finally {
      setIsSaving(false);
    }
  };

  const openRegister = () => {
    setIsFocused(false);
    setForm({
      name: key.toUpperCase(),
      displayname: "",
      abbr: "",
      category: "",
      tier: "",
      address: activePlatform?.branch?.address?.region
        ? { ...activePlatform.branch.address }
        : _address,
    });
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    const { tier, ...branch } = form;
    if (!branch.category || (tiers.length && !tier))
      return Swal.fire({
        icon: "warning",
        title: "Incomplete details",
        text: `Please select a category${tiers.length ? ` and ${tierKey}` : ""}.`,
      });

    setIsSaving(true);
    try {
      const { payload: provider } = await axioKit.save(
        "assets/providers",
        {
          branch: {
            ...branch,
            name: branch.name.toUpperCase(),
            displayname: branch.displayname.toUpperCase(),
            abbr: branch.abbr.toUpperCase(),
          },
          category: scType,
          vendors: branchId,
          ...(tier && { [tierKey]: tier }),
        },
        token,
        "register_branch",
      );
      setForm(null);
      addSource({
        ...provider,
        clients: { affiliated: [], ...provider.clients },
      });
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Registration failed",
        text: error.message,
      });
    } finally {
      setIsSaving(false);
    }
  };

  const showList = isFocused && !disabled;

  return (
    <div className="physicians-search-container">
      <div className="d-flex align-items-center">
        <input
          type="text"
          className="physicians-search-input form-control form-control-sm"
          placeholder={`Search ${isInhouse ? "inhouse branch" : srcName.toLowerCase()}...`}
          value={query}
          disabled={disabled || isSaving}
          onFocus={({ target }) => {
            setIsFocused(true);
            target.select();
          }}
          onBlur={() => setTimeout(() => setIsFocused(false), 200)}
          onChange={handleChange}
          onKeyDown={(e) => {
            if (e.key === "Enter" && isTyping && !isInhouse) {
              e.preventDefault();
              searchRoster();
            }
          }}
        />
        {isSaving && <MDBIcon icon="spinner" pulse className="ml-2" />}
      </div>

      {showList && (
        <ul className="physicians-results-list" style={{ zIndex: 10 }}>
          {!isTyping && !sources.length && (
            <li className="p-2 text-center text-muted small">
              No {isInhouse ? "inhouse branch" : `${srcName} source`} tagged
              yet.
              {!isInhouse && " Type a name to search the roster."}
            </li>
          )}

          {tagged.length > 0 && (
            <>
              {isTyping && (
                <li className="px-2 pt-1 text-muted small">Your sources</li>
              )}
              {tagged.map((source) => (
                <li
                  key={source._id}
                  className="physicians-result-item"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => handlePick(source)}
                >
                  <div className="physicians-result-content">
                    <MDBIcon icon="tag" className="mr-2 text-info" />
                    {getLabel(source)}
                  </div>
                </li>
              ))}
            </>
          )}

          {isTyping && !tagged.length && (
            <li className="p-2 text-center text-muted small">
              🚫 Not found in your {isInhouse ? "inhouse branches" : "sources"}.
            </li>
          )}

          {isTyping && !isInhouse && (
            <>
              {roster === null ? (
                <li
                  className="physicians-result-item"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={searchRoster}
                >
                  <div className="physicians-result-content text-primary">
                    <MDBIcon
                      icon={isSearching ? "spinner" : "search"}
                      pulse={isSearching}
                      className="mr-2"
                    />
                    Search "{key}" in roster
                  </div>
                </li>
              ) : (
                <>
                  <li className="px-2 pt-1 text-muted small">
                    Roster{roster.length ? ", click to tag" : ""}
                  </li>
                  {roster.length ? (
                    roster.map((branch) => (
                      <li
                        key={branch._id}
                        className="physicians-result-item"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => handleTag(branch)}
                      >
                        <div className="physicians-result-content">
                          <MDBIcon icon="database" className="mr-2" />
                          {branchLabel(branch)}
                        </div>
                      </li>
                    ))
                  ) : (
                    <li className="p-2 text-center text-muted small">
                      🚫 "{key}" is not in the roster either.
                    </li>
                  )}
                  <li
                    className="physicians-result-item"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={openRegister}
                  >
                    <div className="physicians-result-content text-primary">
                      <MDBIcon icon="plus" className="mr-2" />
                      Register "{key}" as a new {srcName.toLowerCase()} source
                    </div>
                  </li>
                </>
              )}
            </>
          )}
        </ul>
      )}

      <MDBModal
        size="md"
        isOpen={!!form}
        toggle={() => !isSaving && setForm(null)}
        backdrop
      >
        <MDBModalHeader
          toggle={() => !isSaving && setForm(null)}
          className="light-blue darken-3 white-text"
        >
          <MDBIcon icon="code-branch" className="mr-2" />
          Register {srcName} Source
        </MDBModalHeader>
        <MDBModalBody className="mb-0">
          {form && (
            <form onSubmit={handleRegister}>
              <MDBRow>
                <MDBCol md="6">
                  <Select
                    label="Category"
                    preValue={form.category}
                    onChange={(category) => setForm({ ...form, category })}
                    keys={"value"}
                    values={"text"}
                    collections={categories}
                  />
                </MDBCol>
                <MDBCol md="6">
                  <MDBInput
                    label="Name"
                    required
                    value={form.name}
                    onChange={({ target }) =>
                      setForm({ ...form, name: target.value.toUpperCase() })
                    }
                  />
                </MDBCol>
              </MDBRow>
              <MDBRow style={{ marginTop: "-1rem" }}>
                <MDBCol>
                  <MDBInput
                    label="Acronym"
                    value={form.abbr}
                    onChange={({ target }) =>
                      setForm({ ...form, abbr: target.value.toUpperCase() })
                    }
                  />
                </MDBCol>
                <MDBCol>
                  <MDBInput
                    label="Branch Display name"
                    required
                    value={form.displayname}
                    onChange={({ target }) =>
                      setForm({
                        ...form,
                        displayname: target.value.toUpperCase(),
                      })
                    }
                  />
                </MDBCol>
              </MDBRow>
              {tiers.length > 0 && (
                <MDBRow style={{ marginTop: "-1rem" }}>
                  <MDBCol>
                    <Select
                      label={scType === "mbs" ? "Membership" : "Contract"}
                      preValue={form.tier}
                      onChange={(tier) => setForm({ ...form, tier })}
                      keys={"value"}
                      values={"text"}
                      collections={tiers}
                    />
                  </MDBCol>
                </MDBRow>
              )}
              <AddressSelect
                handleChange={(_, address) =>
                  setForm((prev) => ({
                    ...prev,
                    address: { ...prev.address, ...address },
                  }))
                }
                address={form.address}
              />
              <MDBBtn
                className="float-right mt-3"
                rounded
                type="submit"
                color="info"
                disabled={isSaving}
              >
                Register and Tag
                {isSaving && <MDBIcon icon="spinner" pulse className="ml-2" />}
              </MDBBtn>
            </form>
          )}
        </MDBModalBody>
      </MDBModal>
    </div>
  );
}
